// === GENERATED:SCREEN:PAYMENT-INFORMATION ===
// Generated by Sluitstraat Generator
// Module: jsonforms
// Step: Payment Information (Universal)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useConfig,
  WorkflowPageLayout,
  getCurrentApplicationStepNumber,
  calculateApplicationSteps,
  formatPrice,
  LoadingScreen,
  StartDateField,
  isDebugMode,
  computeAvpDisplayPremium,
  computeWoonhuisDisplayPremium,
} from '@sluitstraat/core';
import { PremiumBreakdownCard } from '../../components/PremiumBreakdownCard';
import { WorkflowDebugPanel } from '../../components/WorkflowDebugPanel';
import { HelpCard } from '../../components/HelpCard';
import { SubscriptionCard } from '../../components/SubscriptionCard';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { USE_BREAKDOWN_UI } from '../../config/uiFlags';
import { computeInboedelTotalFromSections } from '../../utils/inboedelPricing';
import { computePolicyCostAllocation } from '../../utils/policyCost';
import { getProductOrder } from '../../workflowConfig';
import { hasAnySelectedProduct } from '../../utils/productGuards';
import { validateIBAN, validateStartDate } from './payment/validation';
import { computeDoorlopendeReisDisplayPremium } from '../../utils/doorlopendeReisPricing';
import { buildPremiumDebugSection, readDebugIds, readEnrichedDocument } from '../../utils/debugData';
import { paymentInformationStyles as styles } from '../../styles/commonStyles';

const SCREEN_ID = 'payment-information';

export const PaymentInformationScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, paymentData, setPaymentData, selectedProducts, data: workflowData } = useWorkflow();
  const { config } = useConfig();

  // Initialize with payment info data OR create new structure
  const initializePaymentData = () => {
    const savedData = getStepData(SCREEN_ID);
    if (savedData) {
      return savedData;
    }

    // If paymentData exists from context, use it
    if (paymentData && Object.keys(paymentData).length > 0) {
      return paymentData;
    }

    // Start fresh with empty payment info
    return {};
  };

  const [data, setData] = useState<any>(initializePaymentData());

  // Initialize Request data format by loading quote data from ALL selected products
  // Structure matches AcceptanceQuestionsScreen: { avp, woonhuis, inboedel }
  const [requestData, setRequestData] = useState<any>(() => {
    // Get client info for VP fields
    const clientInfo = workflowData?.['client-information']?.clientInfo || {};

    // Build VP data from client information
    const vpData: any = {
      VP_PARZAKC: 'P', // P for Particulier (Individual)
    };
    if (clientInfo.initials) vpData.VP_VOORL = clientInfo.initials;
    if (clientInfo.lastName) vpData.VP_ANAAM = clientInfo.lastName;
    if (clientInfo.affix) vpData.VP_VOORV = clientInfo.affix;
    if (clientInfo.gender) {
      vpData.VP_GESLACH = clientInfo.gender;
      // VP_AANHEFC / VP_TITULAT — zero-padded 5-char gender codes ('00001' = Dhr., '00002' = Mevr.).
      // Explicit clientInfo.salutationCode / clientInfo.titulature take precedence.
      if (!clientInfo.salutationCode) {
        if (clientInfo.gender === 'M') vpData.VP_AANHEFC = '00001';
        else if (clientInfo.gender === 'V') vpData.VP_AANHEFC = '00002';
      }
      if (!clientInfo.titulature) {
        if (clientInfo.gender === 'M') vpData.VP_TITULAT = '00001';
        else if (clientInfo.gender === 'V') vpData.VP_TITULAT = '00002';
      }
    }
    if (clientInfo.salutationCode) vpData.VP_AANHEFC = clientInfo.salutationCode;
    if (clientInfo.titulature) vpData.VP_TITULAT = clientInfo.titulature;
    if (clientInfo.email) vpData.VP_EMAIL = clientInfo.email;
    if (clientInfo.phone) vpData.VP_TELNUM = clientInfo.phone;

    // Add date of birth if available (convert DD-MM-YYYY to YYYYMMDD)
    if (clientInfo.dateOfBirth) {
      const parts = clientInfo.dateOfBirth.split('-');
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        vpData.VP_GEBDAT = parseInt(`${year}${month}${day}`);
      }
    }

    // Helper to ensure VP fields are populated in product data
    const ensureVpFields = (productData: any) => {
      if (productData?.Contractdocument?.PP) {
        if (!productData.Contractdocument.PP.VP) {
          productData.Contractdocument.PP.VP = {};
        }
        Object.assign(productData.Contractdocument.PP.VP, vpData);
      }
    };

    // Load quote data from all selected products
    const combinedData: any = {};

    if (selectedProducts?.aansprakelijkheid) {
      const avpQuote = workflowData?.['aansprakelijkheid'];
      if (avpQuote) {
        combinedData.avp = JSON.parse(JSON.stringify(avpQuote));
        ensureVpFields(combinedData.avp);
      }
    }

    if (selectedProducts?.woonhuis) {
      const woonhuisQuote = workflowData?.['woonhuis'];
      if (woonhuisQuote) {
        combinedData.woonhuis = JSON.parse(JSON.stringify(woonhuisQuote));
        ensureVpFields(combinedData.woonhuis);
      }
    }

    if (selectedProducts?.inboedel) {
      const inboedelQuote = workflowData?.['inboedel'];
      if (inboedelQuote) {
        combinedData.inboedel = JSON.parse(JSON.stringify(inboedelQuote));
        ensureVpFields(combinedData.inboedel);
      }
    }

    if (selectedProducts?.personenauto) {
      const personenautoQuote = workflowData?.['personenauto'];
      if (personenautoQuote) {
        combinedData.personenauto = JSON.parse(JSON.stringify(personenautoQuote));
        ensureVpFields(combinedData.personenauto);
      }
    }

    return combinedData;
  });
  const [_schema, _setSchema] = useState<any>(null);
  const [_uischema, _setUischema] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ibanError, setIbanError] = useState<string | null>(null);
  const [ibanTouched, setIbanTouched] = useState(false);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [startDateTouched, setStartDateTouched] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Validate required data on mount - redirect if missing critical data
  useEffect(() => {
    // Check if at least one product is selected (generic — a hardcoded per-product list
    // here silently blocks any product not enumerated, e.g. doorlopendereis).
    const hasAnyProduct = hasAnySelectedProduct(selectedProducts);
    if (!hasAnyProduct) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Check if client information exists
    const clientInfo = workflowData['client-information']?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
      return;
    }

    // Check if at least one product has been configured (has quote data) — generic over the
    // configured product order (a hardcoded list here silently skips newer products).
    const hasAnyQuoteData = getProductOrder().some(
      (id) => selectedProducts?.[id] && workflowData[id]?.Contractdocument,
    );

    if (!hasAnyQuoteData) {
      // Redirect to the first selected product screen, in the configured product order.
      const firstSelected = getProductOrder().find((id) => selectedProducts?.[id]);
      if (firstSelected) {
        navigate(`/workflow/${firstSelected}`, { replace: true });
      }
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  // Track if initial pre-fill has been done
  const hasPrefilledStartDate = useRef(false);

  // Pre-fill start date from existing PP_INGDAT if available (only once on mount)
  useEffect(() => {
    // Only pre-fill once and only if start date is not already set
    if (hasPrefilledStartDate.current) return;
    if (data?.paymentInfo?.startDate) {
      hasPrefilledStartDate.current = true;
      return;
    }

    // Try to find PP_INGDAT from any selected product
    let ppIngdat: number | null = null;

    // Check woonhuis
    if (selectedProducts?.woonhuis) {
      const woonhuisData = workflowData['woonhuis']?.Contractdocument?.PP;
      ppIngdat = woonhuisData?.PP_INGDAT || woonhuisData?.OP?.OP_INGDAT || null;
    }

    // Check inboedel if not found
    if (!ppIngdat && selectedProducts?.inboedel) {
      const inboedelData = workflowData['inboedel']?.Contractdocument?.PP;
      ppIngdat = inboedelData?.PP_INGDAT || inboedelData?.IP?.IP_INGDAT || null;
    }

    // Check aansprakelijkheid if not found
    if (!ppIngdat && selectedProducts?.aansprakelijkheid) {
      const aansprakelijkheidData = workflowData['aansprakelijkheid']?.Contractdocument?.PP;
      ppIngdat = aansprakelijkheidData?.PP_INGDAT || aansprakelijkheidData?.DA?.DA_INGDAT || null;
    }

    // Check personenauto if not found
    if (!ppIngdat && selectedProducts?.personenauto) {
      const personenautoData = workflowData['personenauto']?.Contractdocument?.PP;
      ppIngdat = personenautoData?.PP_INGDAT || null;
    }

    // Check rechtsbijstand if not found
    if (!ppIngdat && selectedProducts?.rechtsbijstand) {
      const rechtsbijstandData = workflowData['rechtsbijstand']?.Contractdocument?.PP;
      ppIngdat = rechtsbijstandData?.PP_INGDAT || null;
    }

    // Check doorlopende reis if not found (PP_INGDAT is set to today by the DR calc)
    if (!ppIngdat && selectedProducts?.doorlopendereis) {
      const doorlopendereisData = workflowData['doorlopendereis']?.Contractdocument?.PP;
      ppIngdat = doorlopendereisData?.PP_INGDAT || null;
    }

    // Same for ongevallen (the other DFD product — its calc also stamps PP_INGDAT = today)
    if (!ppIngdat && selectedProducts?.ongevallen) {
      const ongevallenData = workflowData['ongevallen']?.Contractdocument?.PP;
      ppIngdat = ongevallenData?.PP_INGDAT || null;
    }

    // Convert PP_INGDAT from N8 format (YYYYMMDD) to DD-MM-YYYY
    if (ppIngdat) {
      const dateStr = ppIngdat.toString();
      if (dateStr.length === 8) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const formattedDate = `${day}-${month}-${year}`;

        hasPrefilledStartDate.current = true;
        // Update data with pre-filled start date
        setData((prevData: any) => ({
          ...prevData,
          paymentInfo: {
            ...prevData?.paymentInfo,
            startDate: formattedDate
          }
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- writes paymentInfo.startDate itself; re-firing on it would loop
  }, [selectedProducts, workflowData]);

  useEffect(() => {
    const loadSchema = async () => {
      try {
        const response = await fetch(`/screens/${SCREEN_ID}.json`);
        if (!response.ok) {
          throw new Error(`Failed to load schema for ${SCREEN_ID}`);
        }
        const screenData = await response.json();

        // Remove all payment info fields from schema since we'll handle them as custom fields
        const modifiedSchema = { ...screenData.jsonFormSchema };
        if (modifiedSchema.properties?.paymentInfo) {
          // Remove all properties - we'll handle them as custom fields
          delete modifiedSchema.properties.paymentInfo;
          // Update required fields at root level
          if (modifiedSchema.required) {
            modifiedSchema.required = modifiedSchema.required.filter((field: string) => field !== 'paymentInfo');
          }
        }

        _setSchema(modifiedSchema);
        _setUischema(screenData.jsonFormUISchema);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      }
    };

    loadSchema();
  }, []);

  // Update Request Data in real-time when IBAN changes
  useEffect(() => {
    const iban = data?.paymentInfo?.iban;
    if (iban && (requestData.avp || requestData.woonhuis || requestData.inboedel)) {
      const updatedRequestData = JSON.parse(JSON.stringify(requestData));
      const cleanIban = iban.replace(/\s/g, ''); // Remove spaces for storage
      // Update IBAN in all products
      if (updatedRequestData.avp?.Contractdocument?.PP) {
        updatedRequestData.avp.Contractdocument.PP.PP_IBAN = cleanIban;
      }
      if (updatedRequestData.woonhuis?.Contractdocument?.PP) {
        updatedRequestData.woonhuis.Contractdocument.PP.PP_IBAN = cleanIban;
      }
      if (updatedRequestData.inboedel?.Contractdocument?.PP) {
        updatedRequestData.inboedel.Contractdocument.PP.PP_IBAN = cleanIban;
      }
      setRequestData(updatedRequestData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- writes requestData itself; re-firing on it would loop
  }, [data?.paymentInfo?.iban]);

  // Update Request Data in real-time when Start Date changes
  useEffect(() => {
    const startDate = data?.paymentInfo?.startDate;
    if (startDate && (requestData.avp || requestData.woonhuis || requestData.inboedel)) {
      // Convert startDate from DD-MM-YYYY to PP_INGDAT (YYYYMMDD N8 format)
      const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[012])-\d{4}$/;
      if (dateRegex.test(startDate)) {
        const dateParts = startDate.split('-');
        const day = dateParts[0];
        const month = dateParts[1];
        const year = dateParts[2];
        const ppIngdat = parseInt(`${year}${month}${day}`);

        const updatedRequestData = JSON.parse(JSON.stringify(requestData)); // Deep clone

        // Update PP_INGDAT in all products
        if (updatedRequestData.avp?.Contractdocument?.PP) {
          updatedRequestData.avp.Contractdocument.PP.PP_INGDAT = ppIngdat;
          if (updatedRequestData.avp.Contractdocument.PP.DA) {
            updatedRequestData.avp.Contractdocument.PP.DA.DA_INGDAT = ppIngdat;
          }
        }
        if (updatedRequestData.woonhuis?.Contractdocument?.PP) {
          updatedRequestData.woonhuis.Contractdocument.PP.PP_INGDAT = ppIngdat;
          if (updatedRequestData.woonhuis.Contractdocument.PP.OP) {
            updatedRequestData.woonhuis.Contractdocument.PP.OP.OP_INGDAT = ppIngdat;
          }
        }
        if (updatedRequestData.inboedel?.Contractdocument?.PP) {
          updatedRequestData.inboedel.Contractdocument.PP.PP_INGDAT = ppIngdat;
          if (updatedRequestData.inboedel.Contractdocument.PP.IP) {
            updatedRequestData.inboedel.Contractdocument.PP.IP.IP_INGDAT = ppIngdat;
          }
        }

        setRequestData(updatedRequestData);

        // Also update the workflow data so quote data reflects the change
        if (selectedProducts?.aansprakelijkheid) {
          const quoteData = workflowData?.['aansprakelijkheid'];
          if (quoteData?.Contractdocument?.PP) {
            const updatedQuoteData = JSON.parse(JSON.stringify(quoteData));
            updatedQuoteData.Contractdocument.PP.PP_INGDAT = ppIngdat;
            if (updatedQuoteData.Contractdocument.PP.DA) {
              updatedQuoteData.Contractdocument.PP.DA.DA_INGDAT = ppIngdat;
            }
            saveStepData('aansprakelijkheid', updatedQuoteData);
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- writes requestData and the product step data itself; re-firing on them would loop
  }, [data?.paymentInfo?.startDate, selectedProducts]);

  // Format IBAN number
  const formatIBAN = (value: string): string => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();

    // Format in groups of 4
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;

    return formatted;
  };


  const handleCustomFieldChange = (field: string, value: any) => {
    let processedValue = value;

    // Apply IBAN formatting
    if (field === 'paymentInfo.iban') {
      processedValue = formatIBAN(value);

      // Clear error while typing
      if (ibanTouched) {
        setIbanError(null);
      }
    }

    // Clear start date error while typing
    if (field === 'paymentInfo.startDate' && startDateTouched) {
      setStartDateError(null);
    }

    const updatedData = { ...data };

    // Handle nested field paths (e.g., 'paymentInfo.iban')
    const fieldParts = field.split('.');
    let current = updatedData;

    for (let i = 0; i < fieldParts.length - 1; i++) {
      if (!current[fieldParts[i]]) {
        current[fieldParts[i]] = {};
      }
      current = current[fieldParts[i]];
    }

    current[fieldParts[fieldParts.length - 1]] = processedValue;

    setData(updatedData);
    // Persist the draft on every change (see ClientInformationScreen): the request data for
    // the products is still only built on Volgende, after validation.
    saveStepData(SCREEN_ID, updatedData);

    // Sync all paymentInfo fields to paymentData so products can access them
    if (field.startsWith('paymentInfo.')) {
      const fieldName = field.replace('paymentInfo.', '');
      setPaymentData({
        ...paymentData,
        paymentInfo: {
          ...paymentData?.paymentInfo,
          [fieldName]: processedValue
        }
      });
    }
  };

  const handleIbanBlur = () => {
    setIbanTouched(true);
    const iban = data?.paymentInfo?.iban || '';

    if (!iban) {
      setIbanError(t('screens.paymentInfo.validation.ibanRequired'));
      return;
    }

    if (!validateIBAN(iban)) {
      setIbanError(t('screens.paymentInfo.validation.ibanInvalid'));
    } else {
      setIbanError(null);
    }
  };

  // Helper to get the appropriate error message for start date validation
  const getStartDateErrorMessage = (errorType: 'empty' | 'invalid' | 'past' | 'tooFar'): string => {
    switch (errorType) {
      case 'empty':
        return t('screens.paymentInfo.validation.startDateRequired');
      case 'invalid':
        return t('screens.paymentInfo.validation.startDateInvalid');
      case 'past':
        return t('screens.paymentInfo.validation.startDatePast');
      case 'tooFar':
        return t('screens.paymentInfo.validation.startDateTooFar');
    }
  };

  const handleStartDateBlur = () => {
    setStartDateTouched(true);
    const startDate = data?.paymentInfo?.startDate || '';

    const validationError = validateStartDate(startDate);
    if (validationError) {
      setStartDateError(getStartDateErrorMessage(validationError));
    } else {
      setStartDateError(null);
    }
  };

  // Mapping of error types to form element IDs for scrolling
  const errorFieldIdMap: Record<string, string> = {
    'iban': 'iban',
    'startDate': 'start-date',
  };

  // Scroll to the first field with a validation error
  const scrollToFirstError = (errorFields: string[]) => {
    for (const field of errorFields) {
      const elementId = errorFieldIdMap[field];
      if (elementId) {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return;
        }
      }
    }
  };

  // Re-translate validation errors when language changes
  useEffect(() => {
    // Update IBAN error if it exists
    if (ibanError) {
      const iban = data?.paymentInfo?.iban || '';
      if (!iban) {
        setIbanError(t('screens.paymentInfo.validation.ibanRequired'));
      } else if (!validateIBAN(iban)) {
        setIbanError(t('screens.paymentInfo.validation.ibanInvalid'));
      }
    }
    // Update start date error if it exists
    if (startDateError) {
      const startDate = data?.paymentInfo?.startDate || '';
      const validationError = validateStartDate(startDate);
      if (validationError) {
        setStartDateError(getStartDateErrorMessage(validationError));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleNext = () => {

    // One-time poliskosten per product, cap-aware allocation (€12,50 car / €6,25 other, max €12,50).
    const policyAllocation = computePolicyCostAllocation(selectedProducts, config);

    let hasErrors = false;
    const errorFields: string[] = [];

    // Validate IBAN
    const iban = data?.paymentInfo?.iban || '';
    if (!iban) {
      setIbanError(t('screens.paymentInfo.validation.ibanRequired'));
      setIbanTouched(true);
      hasErrors = true;
      errorFields.push('iban');
    } else if (!validateIBAN(iban)) {
      setIbanError(t('screens.paymentInfo.validation.ibanInvalid'));
      setIbanTouched(true);
      hasErrors = true;
      errorFields.push('iban');
    } else {
      setIbanError(null);
    }

    // Validate start date
    const startDate = data?.paymentInfo?.startDate || '';
    const startDateValidationError = validateStartDate(startDate);
    if (startDateValidationError) {
      setStartDateError(getStartDateErrorMessage(startDateValidationError));
      setStartDateTouched(true);
      hasErrors = true;
      errorFields.push('startDate');
    } else {
      setStartDateError(null);
    }

    // If any validation failed, scroll to first error and return
    if (hasErrors) {
      scrollToFirstError(errorFields);
      return;
    }

    // Save UI payment data
    saveStepData(SCREEN_ID, data);
    setPaymentData(data);

    // Convert startDate from DD-MM-YYYY to PP_INGDAT (YYYYMMDD N8 format)
    const dateParts = startDate.split('-');
    let ppIngdat: number | null = null;
    if (dateParts.length === 3) {
      const day = dateParts[0];
      const month = dateParts[1];
      const year = dateParts[2];
      ppIngdat = parseInt(`${year}${month}${day}`);
    }

    // Convert and save Request format data for ALL selected products
    const updatedRequestData = JSON.parse(JSON.stringify(requestData)); // Deep clone

    // Strip spaces from IBAN for backend storage
    const cleanIban = iban.replace(/\s/g, '');

    // Update AVP product if selected
    if (selectedProducts?.aansprakelijkheid && updatedRequestData.avp) {
      if (!updatedRequestData.avp.Contractdocument) updatedRequestData.avp.Contractdocument = {};
      if (!updatedRequestData.avp.Contractdocument.PP) updatedRequestData.avp.Contractdocument.PP = {};

      updatedRequestData.avp.Contractdocument.PP.PP_IBAN = cleanIban;
      if (ppIngdat) updatedRequestData.avp.Contractdocument.PP.PP_INGDAT = ppIngdat;
      // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
      updatedRequestData.avp.Contractdocument.PP.PP_EKST = policyAllocation.aansprakelijkheid;

      // Also update DA section for AVP
      if (!updatedRequestData.avp.Contractdocument.PP.DA) updatedRequestData.avp.Contractdocument.PP.DA = {};
      if (ppIngdat) updatedRequestData.avp.Contractdocument.PP.DA.DA_INGDAT = ppIngdat;
    }

    // Update Woonhuis product if selected
    if (selectedProducts?.woonhuis && updatedRequestData.woonhuis) {
      if (!updatedRequestData.woonhuis.Contractdocument) updatedRequestData.woonhuis.Contractdocument = {};
      if (!updatedRequestData.woonhuis.Contractdocument.PP) updatedRequestData.woonhuis.Contractdocument.PP = {};

      updatedRequestData.woonhuis.Contractdocument.PP.PP_IBAN = cleanIban;
      if (ppIngdat) updatedRequestData.woonhuis.Contractdocument.PP.PP_INGDAT = ppIngdat;
      // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
      updatedRequestData.woonhuis.Contractdocument.PP.PP_EKST = policyAllocation.woonhuis;

      // Also update OP section for Woonhuis
      if (!updatedRequestData.woonhuis.Contractdocument.PP.OP) updatedRequestData.woonhuis.Contractdocument.PP.OP = {};
      if (ppIngdat) updatedRequestData.woonhuis.Contractdocument.PP.OP.OP_INGDAT = ppIngdat;
    }

    // Update Inboedel product if selected
    if (selectedProducts?.inboedel && updatedRequestData.inboedel) {
      if (!updatedRequestData.inboedel.Contractdocument) updatedRequestData.inboedel.Contractdocument = {};
      if (!updatedRequestData.inboedel.Contractdocument.PP) updatedRequestData.inboedel.Contractdocument.PP = {};

      updatedRequestData.inboedel.Contractdocument.PP.PP_IBAN = cleanIban;
      if (ppIngdat) updatedRequestData.inboedel.Contractdocument.PP.PP_INGDAT = ppIngdat;
      // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
      updatedRequestData.inboedel.Contractdocument.PP.PP_EKST = policyAllocation.inboedel;

      // Also update IP section for Inboedel
      if (!updatedRequestData.inboedel.Contractdocument.PP.IP) updatedRequestData.inboedel.Contractdocument.PP.IP = {};
      if (ppIngdat) updatedRequestData.inboedel.Contractdocument.PP.IP.IP_INGDAT = ppIngdat;
    }

    // Update Personenauto product if selected
    if (selectedProducts?.personenauto && updatedRequestData.personenauto) {
      if (!updatedRequestData.personenauto.Contractdocument) updatedRequestData.personenauto.Contractdocument = {};
      if (!updatedRequestData.personenauto.Contractdocument.PP) updatedRequestData.personenauto.Contractdocument.PP = {};

      updatedRequestData.personenauto.Contractdocument.PP.PP_IBAN = cleanIban;
      if (ppIngdat) updatedRequestData.personenauto.Contractdocument.PP.PP_INGDAT = ppIngdat;
      // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
      updatedRequestData.personenauto.Contractdocument.PP.PP_EKST = policyAllocation.personenauto;
    }

    // Update Rechtsbijstand product if selected
    if (selectedProducts?.rechtsbijstand && updatedRequestData.rechtsbijstand) {
      if (!updatedRequestData.rechtsbijstand.Contractdocument) updatedRequestData.rechtsbijstand.Contractdocument = {};
      if (!updatedRequestData.rechtsbijstand.Contractdocument.PP) updatedRequestData.rechtsbijstand.Contractdocument.PP = {};

      updatedRequestData.rechtsbijstand.Contractdocument.PP.PP_IBAN = cleanIban;
      if (ppIngdat) updatedRequestData.rechtsbijstand.Contractdocument.PP.PP_INGDAT = ppIngdat;
      // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
      updatedRequestData.rechtsbijstand.Contractdocument.PP.PP_EKST = policyAllocation.rechtsbijstand;
    }

    // Save Request data for all products
    saveStepData('payment-information-request', updatedRequestData);

    // Navigate to acceptance questions (next step in application flow)
    navigate('/workflow/acceptance-questions');
  };

  const handlePrevious = () => {
    saveStepData(SCREEN_ID, data);
    // Navigate back to summary (previous step from payment in application flow)
    navigate('/workflow/summary');
  };

  if (loading) {
    return <LoadingScreen message={t('common.loadingSchema')} />;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>{t('common.errorLoadingSchema')}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} style={styles.button}>
          {t('common.backToHome')}
        </button>
      </div>
    );
  }

  // Calculate dynamic step progress (application flow)
  const currentStep = getCurrentApplicationStepNumber('/workflow/payment-information');
  const totalSteps = calculateApplicationSteps();

  // Generic over the configured order — matches utils/subscriptionFee.ts (a hardcoded list
  // here undercounted rechtsbijstand/doorlopendereis/ongevallen selections for the fee tier).
  const selectedProductCount = getProductOrder().filter((id) => selectedProducts?.[id]).length;

  const subscriptionFeeConfig = (config as any)?.subscriptionFee;
  const subscriptionFee = subscriptionFeeConfig?.enabled
    ? (selectedProductCount > 2 ? (subscriptionFeeConfig.tier3 || 20.00) : (subscriptionFeeConfig.tier1 || 12.50))
    : 0;
  const prolongationFeeConfig = (config as any)?.prolongationFee;
  const prolongationFee = prolongationFeeConfig?.enabled ? (prolongationFeeConfig.amount || 2.50) : 0;

  // Get individual product premium from package-discounted data (as a number).
  const getProductPremiumNumber = (productType: 'aansprakelijkheid' | 'inboedel' | 'woonhuis' | 'personenauto' | 'rechtsbijstand' | 'ongevallen' | 'doorlopendereis'): number => {
    // AVP / Inboedel / Woonhuis: always read from workflow data so the per-product line shows the
    // product's own premium (with its UI discount baked by the product screen) WITHOUT the
    // package-level discount the BFF's package endpoint stacks on top. The package discount is a
    // separate line further down; baking it into the per-product line would double-display a 5%.
    if (productType === 'aansprakelijkheid') {
      const avp = computeAvpDisplayPremium(workflowData?.['aansprakelijkheid'], config as any);
      return avp > 0 ? avp : 0;
    }
    if (productType === 'inboedel') {
      // Use the shared per-section helper so this matches the Inboedel screen's badge sum exactly
      // (e.g. €23,99 with all 6 optional coverages), instead of the helper's single-floor 24,02.
      const inb = computeInboedelTotalFromSections(workflowData?.['inboedel'], config as any);
      return inb > 0 ? inb : 0;
    }
    if (productType === 'woonhuis') {
      const wh = computeWoonhuisDisplayPremium(workflowData?.['woonhuis'], config as any);
      return wh > 0 ? wh : 0;
    }
    if (productType === 'ongevallen') {
      // Same DFD model as DR: ex-provision, tax-exempt (the helper reads OD natively).
      return computeDoorlopendeReisDisplayPremium(workflowData?.['ongevallen']?.Contractdocument?.PP);
    }
    if (productType === 'doorlopendereis') {
      // Ex-provision, tax-exempt price (no UI/package discount).
      return computeDoorlopendeReisDisplayPremium(workflowData?.['doorlopendereis']?.Contractdocument?.PP);
    }
    // Personenauto / Rechtsbijstand: no per-product UI discount; PP_TTOT from package product
    // (or workflow fallback) is the correct customer-facing value.
    try {
      const enrichedDoc = sessionStorage.getItem('enriched_calculation_document');
      if (enrichedDoc) {
        const doc = JSON.parse(enrichedDoc);
        const packageProducts = doc?.Pakket?.[0]?.Onderdeel || [];
        const pkg = productType === 'personenauto'
          ? packageProducts.find((p: any) => p.PP?.OB)
          : packageProducts.find((p: any) => p.PP?.WC && p.PP?.PP_EXTERN === 'RB');
        if (pkg?.PP?.PP_TTOT) return Number(pkg.PP.PP_TTOT) || 0;
      }
    } catch (error) { /* fall back */ }
    const premium = workflowData?.[productType]?.Contractdocument?.PP?.PP_TTOT;
    return premium ? parseFloat(premium) : 0;
  };

  const getProductPremium = (productType: 'aansprakelijkheid' | 'inboedel' | 'woonhuis' | 'personenauto' | 'rechtsbijstand' | 'ongevallen' | 'doorlopendereis'): string => {
    const n = getProductPremiumNumber(productType);
    return n > 0 ? formatPrice(n) : '0,00';
  };

  // Calculate total monthly premium by summing per-product ex-provision helper values.
  // Using PK_TTOT here would overstate the total because PK_TTOT includes the provision,
  // while the individual per-product prices use the ex-provision helper.
  const calculateTotalMonthly = () => {
    const productTypes: Array<'woonhuis' | 'inboedel' | 'aansprakelijkheid' | 'personenauto' | 'rechtsbijstand' | 'ongevallen' | 'doorlopendereis'> = [
      'woonhuis', 'inboedel', 'aansprakelijkheid', 'personenauto', 'rechtsbijstand', 'ongevallen', 'doorlopendereis',
    ];
    let total = 0;
    for (const type of productTypes) {
      if (selectedProducts?.[type]) total += getProductPremiumNumber(type);
    }
    return formatPrice(total + subscriptionFee + prolongationFee);
  };

  // Collected current-page data for the debug panel: ids/relation, premium summary, package,
  // all products (requestData carries the summary-endpoint responses with the IBAN added).
  const debugCollected: Record<string, unknown> = {
    ids: readDebugIds(),
    relation: workflowData?.['client-information']?.clientInfo ?? null,
    payment: data?.paymentInfo ?? null,
    premium: buildPremiumDebugSection(selectedProducts, workflowData, config),
    package: readEnrichedDocument(),
    products: requestData,
  };

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps} flowType="application">
      <main style={styles.page} className="payment-page">
        <div style={styles.backgroundDecoration}></div>
        <div style={styles.container} className="payment-container">
          <header style={styles.contentHeader} className="payment-header">
            <h1 style={styles.title} className="payment-title">{t('screens.paymentInfo.title')}</h1>
            <p style={styles.subtitle} className="payment-subtitle">
              {t('screens.paymentInfo.subtitle')}
            </p>
          </header>

          <div style={styles.contentWrapper} className="payment-content-wrapper">
            {/* Main Content */}
            <div style={styles.mainContent} className="payment-main-content">
              {/* Bankgegevens Section */}
              <div style={styles.sectionContainer} className="payment-section">
            <div style={styles.sectionHeader} className="payment-section-header">
              <svg style={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
              <h2 style={styles.sectionTitle} className="payment-section-title">{t('screens.paymentInfo.sections.bankDetails')}</h2>
            </div>
            <p style={styles.sectionDescription} className="payment-section-description">{t('screens.paymentInfo.sections.bankDetailsDescription')}</p>

            {/* IBAN Field */}
            <div style={styles.fieldContainer} className="payment-field">
              <label style={styles.fieldLabel} className="payment-field-label" htmlFor="iban">
                {t('screens.paymentInfo.fields.iban')} <span style={styles.required}>*</span>
              </label>
              <input
                id="iban"
                type="text"
                className="payment-field-input"
                value={data?.paymentInfo?.iban || ''}
                onChange={(e) => handleCustomFieldChange('paymentInfo.iban', e.target.value)}
                onBlur={handleIbanBlur}
                style={ibanError ? styles.fieldInputError : styles.fieldInput}
                placeholder={t('screens.paymentInfo.placeholders.iban')}
                required
              />
              {ibanError && (
                <div style={styles.errorMessage}>
                  {ibanError}
                </div>
              )}
            </div>

            {/* Start Date Field (PP_INGDAT) */}
            <div style={styles.fieldContainer}>
              <StartDateField
                value={data?.paymentInfo?.startDate || ''}
                onChange={(value) => handleCustomFieldChange('paymentInfo.startDate', value)}
                outputFormat="string"
                required={true}
                id="start-date"
                style={styles.fieldInput}
                labelStyle={styles.fieldLabel}
                errorMessage={startDateError}
                onBlur={handleStartDateBlur}
              />
            </div>

            {/* Security Info */}
            <div style={styles.securityInfo}>
              <div style={styles.securityHeader}>
                <svg style={styles.securityIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <h4 style={styles.securityTitle}>{t('screens.paymentInfo.security.title')}</h4>
              </div>
              <p style={styles.securityDescription}>{t('screens.paymentInfo.security.description')}</p>
            </div>
              </div>


            <WorkflowNavButtons className="payment-navigation" onBack={handlePrevious} onNext={handleNext} />
            </div>

            {/* Sidebar */}
            <aside style={styles.sidebar} className="payment-sidebar" tabIndex={-1}>
              {/* Summary Card — toggle: USE_BREAKDOWN_UI=true shows shared breakdown card, false shows classic product list. */}
              <div style={styles.summaryCard} className="payment-summary-card" tabIndex={-1}>
                <div style={styles.sidebarHeader}>
                  <svg style={styles.sidebarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" tabIndex={-1} aria-hidden="true">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  <h3 style={styles.sidebarTitle} className="payment-sidebar-title">{t('screens.paymentInfo.sidebar.summary')}</h3>
                </div>
                {USE_BREAKDOWN_UI ? (
                  <PremiumBreakdownCard skipChainGate />
                ) : (
                  <>
                    {/* Product list order: Personenauto → Woonhuis → Inboedel → AVP → Rechtsbijstand */}
                    <div style={styles.productList}>
                      {selectedProducts?.personenauto && (
                        <div style={styles.productItem} className="payment-product-item">
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2"/><circle cx="7.5" cy="17" r="2"/><circle cx="16.5" cy="17" r="2"/></svg>
                            <span style={styles.productName} className="payment-product-name">{t('products.personenauto.name')}</span>
                          </div>
                          <span style={styles.productPrice} className="payment-product-price">€{getProductPremium('personenauto')}</span>
                        </div>
                      )}
                      {selectedProducts?.woonhuis && (
                        <div style={styles.productItem} className="payment-product-item">
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            <span style={styles.productName} className="payment-product-name">{t('products.woonhuis.name')}</span>
                          </div>
                          <span style={styles.productPrice} className="payment-product-price">€{getProductPremium('woonhuis')}</span>
                        </div>
                      )}
                      {selectedProducts?.inboedel && (
                        <div style={styles.productItem} className="payment-product-item">
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 9v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9"/><path d="M9 22V12h6v10"/><path d="M2 10.6L12 2l10 8.6"/></svg>
                            <span style={styles.productName} className="payment-product-name">{t('products.inboedel.name')}</span>
                          </div>
                          <span style={styles.productPrice} className="payment-product-price">€{getProductPremium('inboedel')}</span>
                        </div>
                      )}
                      {selectedProducts?.aansprakelijkheid && (
                        <div style={styles.productItem} className="payment-product-item">
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            <span style={styles.productName} className="payment-product-name">{t('products.aansprakelijkheid.name')}</span>
                          </div>
                          <span style={styles.productPrice} className="payment-product-price">€{getProductPremium('aansprakelijkheid')}</span>
                        </div>
                      )}
                      {selectedProducts?.rechtsbijstand && (
                        <div style={styles.productItem} className="payment-product-item">
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M2 7l10-4 10 4M2 7l4 9h4M14 16h4l4-9"/><circle cx="6" cy="16" r="2"/><circle cx="18" cy="16" r="2"/></svg>
                            <span style={styles.productName} className="payment-product-name">{t('products.rechtsbijstand.name')}</span>
                          </div>
                          <span style={styles.productPrice} className="payment-product-price">€{getProductPremium('rechtsbijstand')}</span>
                        </div>
                      )}
                      {selectedProducts?.ongevallen && (
                        <div style={styles.productItem} className="payment-product-item">
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            <span style={styles.productName} className="payment-product-name">{t('products.ongevallen.name')}</span>
                          </div>
                          <span style={styles.productPrice} className="payment-product-price">€{getProductPremium('ongevallen')}</span>
                        </div>
                      )}
                      {selectedProducts?.doorlopendereis && (
                        <div style={styles.productItem} className="payment-product-item">
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            <span style={styles.productName} className="payment-product-name">{t('products.doorlopendereis.name')}</span>
                          </div>
                          <span style={styles.productPrice} className="payment-product-price">€{getProductPremium('doorlopendereis')}</span>
                        </div>
                      )}
                    </div>
                    <div style={styles.productListDivider}></div>
                    <div style={styles.feeList}>
                      <div style={styles.feeRow}>
                        <span style={styles.feeLabel}>{t('subscription.label')}</span>
                        <span style={styles.feeLabel}>€{formatPrice(subscriptionFee)}</span>
                      </div>
                      <div style={styles.feeRow}>
                        <span style={styles.feeLabel}>{t('prolongation.label')}</span>
                        <span style={styles.feeLabel}>€{formatPrice(prolongationFee)}</span>
                      </div>
                    </div>
                    <div style={styles.divider}></div>
                    <div style={styles.totalRow}>
                      <span style={styles.totalLabel} className="payment-total-label">{t('screens.paymentInfo.sidebar.totalMonthly')}</span>
                      <span style={styles.totalAmount} className="payment-total-amount">€{calculateTotalMonthly()}</span>
                    </div>
                    <div style={styles.taxNote}>{t('screens.offerSummary.includingInsuranceTax')}</div>
                  </>
                )}
              </div>

              {/* Help Card */}
              <HelpCard className="payment-help-card" />
              <SubscriptionCard className="payment-benefits-card" />
            </aside>

          </div>

          {isDebugMode() && <WorkflowDebugPanel collected={debugCollected} />}

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default PaymentInformationScreen;
